import { useMemo } from 'react';
import {
  makeStyles,
  tokens,
  Text,
  Spinner,
  Button,
  ProgressBar,
  Table,
  TableHeader,
  TableRow,
  TableHeaderCell,
  TableBody,
  TableCell,
} from '@coreai/fluentui-react';
import { GiftRegular } from '@fluentui/react-icons';
import { getIsFreeProject, type AutomationProject, type FreeGrantBalance } from '@/types/automation';
import { useFreeGrantBalances } from '@/hooks/useFreeGrantBalances';

const useStyles = makeStyles({
  card: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalM,
    // Don't let a flex parent compress the card; it should keep its content
    // height rather than shrinking to share vertical space with siblings.
    flexShrink: 0,
    marginTop: tokens.spacingVerticalXL,
    padding: tokens.spacingVerticalL,
    backgroundColor: tokens.colorNeutralBackground1,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    borderRadius: tokens.borderRadiusMedium,
    boxShadow: tokens.shadow4,
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
  },
  headerIcon: {
    display: 'inline-flex',
    alignItems: 'center',
    color: tokens.colorNeutralForeground2,
  },
  title: {
    margin: 0,
    fontSize: tokens.fontSizeBase400,
    lineHeight: tokens.lineHeightBase400,
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground1,
  },
  loadingRow: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
    color: tokens.colorNeutralForeground3,
  },
  errorText: {
    color: tokens.colorStatusDangerForeground1,
  },
  errorContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: tokens.spacingVerticalS,
  },
  emptyText: {
    color: tokens.colorNeutralForeground3,
  },
  usageCell: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXXS,
    minWidth: 0,
  },
  usageLabel: {
    color: tokens.colorNeutralForeground3,
    fontSize: tokens.fontSizeBase200,
    lineHeight: tokens.lineHeightBase200,
  },
  numeric: {
    fontVariantNumeric: 'tabular-nums',
  },
  meterName: {
    fontWeight: tokens.fontWeightMedium,
    color: tokens.colorNeutralForeground1,
  },
});

const numberFormat = new Intl.NumberFormat(undefined, { maximumFractionDigits: 2 });
const formatQuantity = (value: number): string =>
  Number.isFinite(value) ? numberFormat.format(value) : '—';

/**
 * Fraction of the grant consumed, clamped to [0, 1]. Guards a zero/negative
 * limit (returns 0) so the progress bar never divides by zero or overflows.
 */
const usedFraction = (balance: FreeGrantBalance): number => {
  if (!(balance.limit > 0)) return 0;
  return Math.min(1, Math.max(0, balance.used / balance.limit));
};

export interface FreeGrantBalancesProps {
  /** The current project. Balances are only fetched for free-grant projects. */
  project: AutomationProject | null | undefined;
}

/**
 * Card showing free-grant meter balances (limit / used / remaining) for a
 * free-grant automation project. Renders nothing for regular projects — the
 * underlying query is gated on `getIsFreeProject(project)`.
 */
export const FreeGrantBalances = ({ project }: FreeGrantBalancesProps) => {
  const styles = useStyles();
  const { balances, isLoading, error, refetch } = useFreeGrantBalances(project);

  const rows = useMemo(
    () =>
      balances.map((balance) => ({
        balance,
        fraction: usedFraction(balance),
      })),
    [balances],
  );

  // This card is meaningful only for free-grant projects. For a paid project the
  // hook stays disabled, so render nothing rather than an empty card. A free
  // project that legitimately returns no meter rows still renders — with the
  // explicit empty state below — so the tab is never a blank panel.
  if (!getIsFreeProject(project)) {
    return null;
  }

  return (
    <section className={styles.card} aria-label="Free grant balances">
      <div className={styles.header}>
        <span className={styles.headerIcon} aria-hidden="true">
          <GiftRegular />
        </span>
        <Text as="h2" className={styles.title}>
          Free grant balances
        </Text>
      </div>

      {isLoading ? (
        <div className={styles.loadingRow} role="status">
          <Spinner size="tiny" />
          <Text size={200}>Loading free grant balances…</Text>
        </div>
      ) : error ? (
        <div className={styles.errorContainer} role="alert">
          <Text className={styles.errorText}>
            Failed to load free grant balances. Please try again.
          </Text>
          <Button size="small" appearance="secondary" onClick={() => refetch()}>
            Retry
          </Button>
        </div>
      ) : balances.length === 0 ? (
        <Text className={styles.emptyText}>
          No free grant meter balances are available for this project yet.
        </Text>
      ) : (
        <Table size="small" aria-label="Free grant meter balances">
          <TableHeader>
            <TableRow>
              <TableHeaderCell>Meter</TableHeaderCell>
              <TableHeaderCell>Usage</TableHeaderCell>
              <TableHeaderCell>Remaining</TableHeaderCell>
              <TableHeaderCell>Limit</TableHeaderCell>
              <TableHeaderCell>Resets</TableHeaderCell>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map(({ balance, fraction }) => (
              <TableRow key={balance.meter}>
                <TableCell>
                  <Text className={styles.meterName}>{balance.meter}</Text>
                </TableCell>
                <TableCell>
                  <div className={styles.usageCell}>
                    <span className={styles.usageLabel}>
                      {formatQuantity(balance.used)} of {formatQuantity(balance.limit)} used
                    </span>
                    <ProgressBar
                      value={fraction}
                      max={1}
                      thickness="medium"
                      color={fraction >= 1 ? 'error' : fraction >= 0.9 ? 'warning' : 'brand'}
                      aria-label={`${balance.meter}: ${formatQuantity(balance.used)} of ${formatQuantity(balance.limit)} used`}
                    />
                  </div>
                </TableCell>
                <TableCell className={styles.numeric}>{formatQuantity(balance.remaining)}</TableCell>
                <TableCell className={styles.numeric}>{formatQuantity(balance.limit)}</TableCell>
                <TableCell>{balance.resetCadence}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </section>
  );
};
